import type {PropertyRuleTimeRange, PropertyTypeRule} from "../types";

/** Sentinel for “citations allowed 24/7” for this type on the property. */
export const CITATION_FULL_DAY_RANGE: PropertyRuleTimeRange = {start: "00:00", end: "00:00"};

/** Default window when the user leaves “24 hours” to set a custom range. */
export const CITATION_CUSTOM_RANGE_DEFAULT: PropertyRuleTimeRange = {start: "22:00", end: "08:00"};

/** `input[type=time]` may yield `HH:MM:SS`; API expects `HH:MM`. */
export function normalizeHHMM(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  return s.length >= 5 ? s.slice(0, 5) : s;
}

export function normalizeRangeHoursFromApi(value: unknown): PropertyRuleTimeRange {
  if (value == null) return {...CITATION_FULL_DAY_RANGE};

  if (value && typeof value === "object" && "start" in value && "end" in value) {
    const o = value as Record<string, unknown>;
    return {
      start: normalizeHHMM(String(o.start ?? "")),
      end: normalizeHHMM(String(o.end ?? "")),
    };
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === "object" && "start" in parsed && "end" in parsed) {
        const o = parsed as Record<string, unknown>;
        return {
          start: normalizeHHMM(String(o.start ?? "")),
          end: normalizeHHMM(String(o.end ?? "")),
        };
      }
    } catch {
      /* ignore */
    }
  }
  return {...CITATION_FULL_DAY_RANGE};
}

export function isFullDayRange(r: PropertyRuleTimeRange | undefined): boolean {
  if (!r) return true;
  return r.start === "00:00" && r.end === "00:00";
}

export function formatCitationRangeHoursLabel(r: PropertyRuleTimeRange | undefined): string {
  const range = r ?? CITATION_FULL_DAY_RANGE;
  if (isFullDayRange(range)) return "24 hours";
  const a = range.start || "—";
  const b = range.end || "—";
  return `${a}–${b}`;
}

export function valueLooksLikeTimeRange(value: unknown): boolean {
  if (value && typeof value === "object" && !Array.isArray(value) && "start" in value && "end" in value) {
    return true;
  }
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Boolean(
        parsed &&
          typeof parsed === "object" &&
          "start" in (parsed as object) &&
          "end" in (parsed as object),
      );
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Detects hour-range rules even when the API does not use `type: "time_range"`.
 * Shared by property Features configurations and safelistings coverage preview.
 */
export function isTimeRangePropertyTypeRule(configuration: {
  type?: string;
  name?: string | null;
  key?: string | null;
  value?: unknown;
}): boolean {
  if (configuration.type === "time_range") return true;
  if (configuration.type === "boolean" || configuration.type === "number") return false;

  const name = configuration.name ?? "";
  const key = configuration.key ?? "";
  if ((/\brange\b/i.test(name) && /\bhour/i.test(name)) || (/\brange/i.test(key) && /\bhour/i.test(key))) {
    return true;
  }

  return valueLooksLikeTimeRange(configuration.value);
}

/** Matches Features tab default when a time_range rule has no stored value yet. */
export function resolvePropertyTypeTimeRangeValue(
  value: unknown,
  whenMissing: PropertyRuleTimeRange = CITATION_CUSTOM_RANGE_DEFAULT,
): PropertyRuleTimeRange {
  if (value !== undefined && value !== null) {
    return normalizeRangeHoursFromApi(value);
  }
  return {...whenMissing};
}

export function isSafelistingsRangePropertyRuleKey(key: string): boolean {
  const k = key.toLowerCase();
  if (k === "safelistings_range_hours" || k === "range_hours") return true;
  return k.includes("safelistings") && /\brange/i.test(k) && /\bhour/i.test(k);
}
